import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { showPageLoading, showToastMessage } from 'enable-ui';
import { ApiStoreKey } from 'enums';
import { useFormik } from 'formik';

import { Station } from 'modules/institution-info/core/type';
import { useFetchApiClassDetail } from 'modules/learners/core/hook';

import type { Classes } from '../type';

import { getExtFileName, hasEmptyValues, randomString, trimValue } from 'helpers';
import { UploadFile } from 'helpers/upload-file';
import { apiCall, closeLoading, useFetchStore, valueByStateKeySelector } from 'hooks/service';
import { useWidthAndHeightFile } from 'hooks/widthAndHeightFile';
import { isEmpty } from 'lodash';

import { defaultClassesForm } from '../constant';

export const useClassesDetailController = () => {
  const params = useParams();

  const { id } = params;

  const { fetchApiClassDetail } = useFetchApiClassDetail(id ?? '');

  const classes: Classes = useFetchStore(valueByStateKeySelector(ApiStoreKey.class));

  const [stations, setStations] = useState<Station[]>([]);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [selectedStation, setSelectedStation] = useState<{ value: string; label: string } | null>(
    null,
  );
  const [initialStation, setInitialStation] = useState<{ value: string; label: string } | null>(
    null,
  );

  const station = stations.find((el) => el.id === selectedStation?.value);

  const { handleGetWidthAndHeightThumbnail, thumbnailHeight, thumbnailWidth } =
    useWidthAndHeightFile();

  const getStations = async (institution_id: string) => {
    const result: Station[] = await apiCall({
      url: '/stations',
      method: 'GET',
      data: {
        institutionId: institution_id,
      },
    });

    if (isEmpty(result)) {
      return;
    }

    setStations(result);
  };

  const formik = useFormik({
    initialValues: defaultClassesForm,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      showPageLoading();
      const request: any = {
        name: trimValue(values.name),
        description: trimValue(values.description),
        stationId: selectedStation?.value,
      };

      if (!values.thumbnail) {
        request.isRemoveThumbnail = true;
      }

      if (thumbnail) {
        const ext = getExtFileName(thumbnail.name);

        const nameFileRandom = `thumbnail-class-${randomString(4)}-${Date.now()}${ext}`;

        await UploadFile(thumbnail, nameFileRandom);

        request.thumbnail = nameFileRandom;

        if (request.thumbnail && thumbnailHeight.value && thumbnailWidth.value) {
          request.thumbnailHeight = thumbnailHeight.value;
          request.thumbnailWidth = thumbnailWidth.value;
        }
      }

      const result: Classes = await apiCall({
        url: `/classes/${id}`,
        method: 'PATCH',
        data: request,
        showError: true,
      });

      if (result?.id) {
        showToastMessage('success', 'Updated class successfully!');

        fetchApiClassDetail();
      }

      closeLoading();
    },
  });

  const { values, isSubmitting, isValid, handleSubmit, handleChange, setValues } = formik;

  useEffect(() => {
    if (isEmpty(classes)) {
      return;
    }

    setValues({
      name: classes.name,
      isActive: classes.isActive,
      description: classes.description,
      code: classes.code,
      thumbnail: classes.thumbnail || '',
      institution: classes.institution?.name,
    });

    const initialStationValue = classes?.station?.stationId
      ? { value: classes?.station?.stationId, label: classes?.station?.centerName ?? '' }
      : null;

    setSelectedStation(initialStationValue);
    setInitialStation(initialStationValue);
  }, [JSON.stringify(classes)]);

  useEffect(() => {
    getStations(classes.institution?.id || '');
  }, [classes.institution?.id]);

  const onCancel = () => {
    setValues({
      name: classes.name,
      isActive: classes.isActive,
      description: classes.description,
      code: classes.code,
      thumbnail: classes.thumbnail || '',
      institution: classes.institution?.name ?? '',
    });
    setSelectedStation(initialStation);
  };

  const thumbnailClasses = classes.thumbnail || '';

  const disabled =
    isSubmitting ||
    !isValid ||
    (values.name === classes?.name &&
      values.description === classes?.description &&
      values.thumbnail === thumbnailClasses &&
      selectedStation?.value === initialStation?.value) ||
    hasEmptyValues({ name: values.name });

  const onChangeThumbnail = (files: File[]) => {
    setThumbnail(files[0]);
    setValues({
      ...values,
      thumbnail: URL.createObjectURL(files[0]),
    });
  };

  const onDeleteThumbnail = () => {
    setThumbnail(null);
    setValues({
      ...values,
      thumbnail: '',
    });
  };

  const onChangeStation = (value: any) => setSelectedStation(value);

  const mappingStations = useMemo(
    () => stations.map((el) => ({ value: el.id, label: el.name })),
    [stations],
  );

  return {
    ...values,
    station,
    stations,
    mappingStations,
    selectedStation,
    onChangeStation,
    classes,
    handleChange,
    handleSubmit,
    onCancel,
    disabled,
    classCompletion: classes?.classCompletion,
    onChangeThumbnail,
    onDeleteThumbnail,
    handleGetWidthAndHeightThumbnail,
  };
};
